import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

/**
 * Sign-in surface used only by the MCP OAuth consent flow.
 *
 * The public /login page is an SEO content page about signing in to
 * DeepSeek.com, so the MCP flow gets its own dot-prefixed route that is never
 * indexed and never linked from site navigation.
 */

/** Only accept a same-origin relative path as the post-sign-in destination. */
const safeNext = (raw: string | null): string => {
  if (!raw) return "/";
  if (!raw.startsWith("/") || raw.startsWith("//")) return "/";
  return raw;
};

const OAuthSignIn = () => {
  const [params] = useSearchParams();
  const next = safeNext(params.get("next"));

  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Already signed in (or just returned from the magic link) — continue the flow.
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) window.location.href = next;
    });
  }, [next]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);

    const { error: signInError } = await supabase.auth.signInWithOtp({
      email,
      options: {
        // Send the user straight back to the consent URL after the magic link.
        emailRedirectTo: `${window.location.origin}${next}`,
      },
    });

    setBusy(false);
    if (signInError) {
      setError(signInError.message);
      return;
    }
    setSent(true);
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4 py-16">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-foreground mb-2">Sign in to continue</h1>
        <p className="text-sm text-muted-foreground">
          An app is trying to connect to the DeepSeek Fan Hub MCP server. Sign in so the connection
          is made as you.
        </p>

        {sent ? (
          <p className="mt-6 text-sm text-foreground">
            Check <span className="font-medium">{email}</span> for a sign-in link. Opening it brings
            you back here to approve the connection.
          </p>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block text-sm font-medium text-foreground" htmlFor="mcp-email">
              Email address
            </label>
            <input
              id="mcp-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground"
              placeholder="you@example.com"
            />
            <button
              type="submit"
              disabled={busy}
              className="w-full rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50"
            >
              {busy ? "Sending…" : "Email me a sign-in link"}
            </button>
            {error && <p className="text-sm text-destructive">{error}</p>}
          </form>
        )}
      </div>
    </main>
  );
};

export default OAuthSignIn;
